import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, CircleDashed, Copy, FileText, Loader2, Play, RefreshCw, TriangleAlert, Wrench, X } from 'lucide-react';
import { ConfirmModal } from '../../../ui/Modal';
import RunLogView from '../RunLogView';
import { useRunStore } from '../runStore';
import { attentionItems, fixLines, hintsFor, safeSteps, sortItems } from './doctorModel';
import { setupKey } from './useRepoDoctor';

export function copyText(text) {
  try {
    const p = navigator.clipboard?.writeText(text);
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch {
    // no clipboard: the text is on screen to select
  }
}

/** One copyable command line. */
export function CommandLine({ text, testId }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <span className="pj-doc-cmd" data-testid={testId}>
      <code>{text}</code>
      <button
        type="button"
        className="pj-doc-copy"
        aria-label={copied ? t('projects.run.doctor.copied') : t('projects.run.doctor.copy')}
        title={copied ? t('projects.run.doctor.copied') : t('projects.run.doctor.copy')}
        onClick={() => {
          copyText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
    </span>
  );
}

function StatusIcon({ status }) {
  if (status === 'ok') return <Check size={14} className="pj-doc-icon pj-doc-icon--ok" aria-label="ok" />;
  if (status === 'error') return <X size={14} className="pj-doc-icon pj-doc-icon--error" aria-label="error" />;
  if (status === 'warn') return <TriangleAlert size={13} className="pj-doc-icon pj-doc-icon--warn" aria-label="warning" />;
  return <CircleDashed size={13} className="pj-doc-icon pj-doc-icon--muted" aria-label={status} />;
}

/** The button label for an item's fix. */
function fixLabel(t, item) {
  const fix = item.fix;
  if (fix.action === 'env_copy') return t('projects.run.doctor.fix.envCopy');
  if (fix.action === 'open_file') return t('projects.run.doctor.fix.openFile', { file: fix.file.split('/').pop() });
  if (fix.action === 'use_port') return t('projects.run.useAnotherPort');
  if (item.kind === 'deps') return t('projects.run.doctor.fix.install');
  if (item.kind === 'submodules') return t('projects.run.doctor.fix.checkout');
  if (item.kind === 'service') return t('projects.run.doctor.fix.compose');
  return t('projects.run.doctor.fix.run');
}

export function itemText(t, item) {
  return t(`projects.run.doctor.codes.${item.code}`, { ...item.params, defaultValue: item.code });
}

function DoctorItem({ repoPath, item, running, failed, onFix, multiFolder }) {
  const { t } = useTranslation();
  const task = useRunStore((s) => s.runs[setupKey(repoPath, item)]);
  const [logOpen, setLogOpen] = useState(false);
  const lines = item.status === 'ok' ? [] : fixLines(item);
  const hints = item.status === 'ok' ? [] : hintsFor(item);
  const where = multiFolder && item.cwd ? item.cwd : null;
  const busy = running === item.id;
  const blocked = item.status === 'blocked';
  return (
    <li className={`pj-doc-item pj-doc-item--${item.status}`} data-testid="doctor-item" data-id={item.id} data-status={item.status}>
      <StatusIcon status={item.status} />
      <div className="pj-doc-body">
        <div className="pj-doc-title">
          <span>{itemText(t, item)}</span>
          {where && <span className="pj-doc-where">{where}</span>}
          {blocked && <span className="pj-doc-where">{t('projects.run.doctor.afterService')}</span>}
        </div>
        {item.status !== 'ok' && item.fix?.action !== 'open_file' && item.fix?.action !== 'use_port' && lines.map((l) => <CommandLine key={l} text={l} testId="doctor-command" />)}
        {hints.length > 0 && (
          <div className="pj-doc-hints" data-testid="doctor-hints">
            <span className="pj-doc-hints-label">{item.kind === 'service' ? t('projects.run.doctor.startHint') : t('projects.run.doctor.installHint')}</span>
            {hints.map((h) => (
              <CommandLine key={h.command} text={h.command} testId="doctor-hint" />
            ))}
          </div>
        )}
        {failed && (
          <span className="pj-doc-failed" role="alert">
            {failed.exitCode != null ? t('projects.run.doctor.fixFailedCode', { code: failed.exitCode }) : t('projects.run.doctor.fixFailed', { error: failed.error || '' })}
          </span>
        )}
        {task && (
          <button type="button" className="pj-doc-loglink" onClick={() => setLogOpen((v) => !v)} data-testid="doctor-log-toggle">
            <FileText size={12} /> {logOpen || busy ? t('projects.run.hideLogs') : t('projects.run.showLogs')}
          </button>
        )}
        {task && (logOpen || busy) && <RunLogView log={task.log} />}
      </div>
      {item.fix && item.status !== 'ok' && item.status !== 'suggest' ? (
        <button type="button" className="gd-btn pj-doc-fix" onClick={() => onFix(item)} disabled={!!running || blocked} data-testid="doctor-fix">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Wrench size={13} />} {fixLabel(t, item)}
        </button>
      ) : item.fix && (item.status === 'suggest' || item.fix.action === 'open_file') ? (
        <button type="button" className="gd-btn pj-doc-fix pj-doc-fix--quiet" onClick={() => onFix(item)} disabled={!!running} data-testid="doctor-fix">
          {busy ? <Loader2 size={13} className="animate-spin" /> : null} {fixLabel(t, item)}
        </button>
      ) : null}
    </li>
  );
}

/**
 * The checklist: ✓/✗ per item with the exact command and a fix button,
 * "Fix all safe items" (project-local steps only), install hints with a
 * copy button for toolchains. Toolchains, services, docker and migrations
 * are one item at a time.
 */
export default function RunDoctorPanel({ repo, d, liveNames, onFix, onRunAll, onStartAnyway, onClose }) {
  const { t } = useTranslation();
  const { doctor, running, fixError } = d;
  const report = doctor.report;
  const items = sortItems(report?.items || []).filter((i) => !(i.kind === 'port' && i.configs.some((n) => liveNames.has(n))));
  const attention = attentionItems(report, { liveNames });
  const safe = safeSteps(report);
  const multiFolder = new Set(items.map((i) => i.cwd)).size > 1;
  const [showOk, setShowOk] = useState(false);
  const problems = items.filter((i) => i.status !== 'ok');
  const ok = items.filter((i) => i.status === 'ok');
  return (
    <div className="pj-doc" data-testid="doctor-panel">
      <div className="pj-doc-head">
        <span className={`pj-doc-summary${attention.length ? ' pj-doc-summary--bad' : ''}`} data-testid="doctor-summary">
          {doctor.checking && !report ? (
            <>
              <Loader2 size={13} className="animate-spin" /> {t('projects.run.doctor.checking')}
            </>
          ) : attention.length ? (
            t('projects.run.doctor.attention', { count: attention.length })
          ) : (
            <>
              <Check size={14} className="pj-doc-icon--ok" /> {t('projects.run.doctor.allGood')}
            </>
          )}
        </span>
        <span className="pj-doc-head-actions">
          {safe.length > 0 && (
            <button type="button" className="gd-btn gd-btn--primary" onClick={d.fixAll} disabled={!!running} data-testid="doctor-fix-all">
              <Wrench size={13} /> {t('projects.run.doctor.fixAll')}
            </button>
          )}
          {(doctor.offerRun || (doctor.pending && attention.every((i) => i.status !== 'error'))) && (
            <button type="button" className="gd-btn gd-btn--primary" onClick={onRunAll} data-testid="doctor-run-all">
              <Play size={13} /> {doctor.pending ? t('projects.run.doctor.startNow') : t('projects.run.runAll')}
            </button>
          )}
          {doctor.pending && attention.some((i) => i.status === 'error') && (
            <button type="button" className="gd-btn" onClick={onStartAnyway} data-testid="doctor-start-anyway">
              <Play size={13} /> {t('projects.run.doctor.startAnyway')}
            </button>
          )}
          <button
            type="button"
            className="graph-icon-btn pj-run-btn"
            onClick={() => d.recheck({ force: true })}
            aria-label={t('projects.run.doctor.recheck')}
            title={t('projects.run.doctor.recheck')}
            data-testid="doctor-recheck"
          >
            <RefreshCw size={13} className={doctor.checking ? 'animate-spin' : ''} />
          </button>
          <button type="button" className="graph-icon-btn pj-run-btn" onClick={onClose} aria-label={t('common.close')} title={t('common.close')} data-testid="doctor-close">
            <X size={13} />
          </button>
        </span>
      </div>
      {doctor.pending && attention.some((i) => i.status === 'error') && (
        <p className="pj-result-meta pj-doc-note" data-testid="doctor-held">
          {t('projects.run.doctor.held', { names: doctor.pending.map((p) => p.config.name).join(', ') })}
        </p>
      )}
      {doctor.error && <div className="graph-error">{t('projects.run.doctor.checkFailed', { error: doctor.error })}</div>}
      <ul className="pj-doc-list">
        {problems.map((item) => (
          <DoctorItem
            key={item.id}
            repoPath={repo.path}
            item={item}
            running={running}
            failed={fixError?.id === item.id ? fixError : null}
            onFix={onFix}
            multiFolder={multiFolder}
          />
        ))}
        {(showOk || problems.length === 0) &&
          ok.map((item) => <DoctorItem key={item.id} repoPath={repo.path} item={item} running={running} onFix={onFix} multiFolder={multiFolder} />)}
      </ul>
      {problems.length > 0 && ok.length > 0 && (
        <button type="button" className="pj-doc-loglink pj-doc-showok" onClick={() => setShowOk((v) => !v)} data-testid="doctor-show-ok">
          {showOk ? t('projects.run.doctor.hideOk') : t('projects.run.doctor.showOk', { count: ok.length })}
        </button>
      )}
    </div>
  );
}

/** "Run these setup commands?" — every command shown before it runs (checklist, Fix all, failed-run banner). */
export function DoctorConfirm({ repo, d }) {
  const { t } = useTranslation();
  return (
    <ConfirmModal
      isOpen={!!d.confirm}
      onClose={d.cancelFix}
      onConfirm={d.confirmFix}
      type="warning"
      title={t('projects.run.doctor.confirmTitle')}
      message={
        <span data-testid="doctor-confirm">
          {t('projects.run.doctor.confirmMessage', { repo: repo.name || repo.path })}
          {(d.confirm?.items || []).flatMap((item) =>
            fixLines(item).map((line, i) => (
              <span key={`${item.id}|${i}`} className="pj-run-approve-cmd">
                <span className="pj-run-approve-name">{itemText(t, item)}</span>
                <code data-testid="doctor-confirm-command">{line}</code>
                {item.fix.cwd && <span className="pj-run-approve-meta">{t('projects.run.approveCwd', { cwd: item.fix.cwd })}</span>}
              </span>
            ))
          )}
        </span>
      }
      confirmText={t('projects.run.approveConfirm')}
      cancelText={t('common.cancel')}
    />
  );
}
